import { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from 'providers/AuthProvider';
import { weddingPaths } from 'routes/paths';

import { getHomePath, isAuthorizedForPath } from 'utils/auth';
import { getPostLogoutPath } from 'utils/post-logout';

interface AuthGuardProps extends PropsWithChildren {
  skipVerification?: boolean;
}

/**
 * AuthGuard - Protects routes that require authentication
 * Also enforces email verification for couple users unless skipVerification is set
 */
const AuthGuard = ({ children, skipVerification }: AuthGuardProps) => {
  const { sessionUser } = useAuth();
  const location = useLocation();

  if (sessionUser) {
    // If authenticated, check if user has permission for this specific path
    if (!isAuthorizedForPath(sessionUser, location.pathname)) {
      // If not authorized (e.g. guest trying to access couple pages),
      // redirect to their appropriate landing page.
      return <Navigate to={getHomePath(sessionUser)} replace />;
    }

    // Sandbox demo couples are created already email-verified; this defensive
    // bypass ensures a demo can never be trapped on the verify-email page even
    // if that assumption changes.
    if (sessionUser.isSandbox) {
      return children;
    }

    // Enforce email verification for couple users (unless skipVerification is set)
    if (
      !skipVerification &&
      sessionUser.role === 'couple' &&
      sessionUser.isEmailVerified === false &&
      location.pathname !== weddingPaths.coupleVerifyEmail
    ) {
      return <Navigate to={weddingPaths.coupleVerifyEmail} replace />;
    }

    // If user is verified but on verify-email page, redirect to dashboard
    if (
      skipVerification &&
      sessionUser.role === 'couple' &&
      sessionUser.isEmailVerified === true &&
      location.pathname === weddingPaths.coupleVerifyEmail
    ) {
      return <Navigate to={weddingPaths.coupleDashboard} replace />;
    }

    return children;
  }

  // Unauthenticated: pick destination from the persisted role hint, NOT the URL.
  // The /couple base is a safe fast-path (an unauthenticated visitor on a couple
  // route belongs at couple login regardless of any prior session).
  if (location.pathname.startsWith(weddingPaths.coupleBase)) {
    return <Navigate to={weddingPaths.coupleLogin} replace />;
  }

  return <Navigate to={getPostLogoutPath()} replace />;
};

export default AuthGuard;
